import { useCallback, useMemo } from "react";
import type { Item } from "~/types/api";
import type { TabWindow } from "../../task.types";
import { calculateNewSelection } from "../utils/selection-helper";

type Props<T> = {
  setWindows: React.Dispatch<React.SetStateAction<TabWindow<T>[]>>;
  windowsRef: React.RefObject<TabWindow<T>[]>;
  activePanelWindowId: string | null;
  setActivePanelWindowId: React.Dispatch<React.SetStateAction<string | null>>;
  setDetailViewItem: React.Dispatch<React.SetStateAction<Item[] | null>>;
  updateDetailViewForWindow: (
    windowId: string,
    newSelectedItems?: Set<number>,
    fetchFresh?: boolean
  ) => Promise<void>;
};

export const useItemSelection = <T>({
  setWindows,
  windowsRef,
  activePanelWindowId,
  setActivePanelWindowId,
  setDetailViewItem,
  updateDetailViewForWindow,
}: Props<T>) => {
  const handleItemSelection = useCallback(
    (
      windowId: string,
      itemIdOrSet: number | Set<number>,
      multiSelect: boolean,
      fetchFresh = false
    ) => {
      if (activePanelWindowId && activePanelWindowId !== windowId) {
        setActivePanelWindowId(null);
        setDetailViewItem(null);
      }

      const currentWindow = windowsRef.current.find((w) => w.id === windowId);
      if (!currentWindow) return;

      const newSelectedItems =
        itemIdOrSet instanceof Set
          ? itemIdOrSet
          : calculateNewSelection(
              currentWindow.state.selectedItems || new Set(),
              itemIdOrSet,
              multiSelect
            );

      setWindows((prev) =>
        prev.map((window) => {
          if (window.id === windowId && window.state) {
            return { ...window, state: { ...window.state, selectedItems: newSelectedItems } };
          }
          return window.state?.selectedItems?.size
            ? { ...window, state: { ...window.state, selectedItems: new Set() } }
            : window;
        })
      );

      if (newSelectedItems.size === 0) {
        setDetailViewItem(null);
      } else {
        updateDetailViewForWindow(windowId, newSelectedItems, fetchFresh);
      }
    },
    [
      activePanelWindowId,
      updateDetailViewForWindow,
      setWindows,
      windowsRef,
      setActivePanelWindowId,
      setDetailViewItem,
    ]
  );

  return useMemo(() => ({ handleItemSelection }), [handleItemSelection]);
};
